import React from 'react'
import { Trophy, Target, Lightbulb, Bot, Clock, TrendingUp } from 'lucide-react'
import { LessonStats } from '../../services/stats/LessonStatsService'

interface LessonStatsDisplayProps {
  stats: LessonStats
  duration: number
  accuracy: number
  progress: number
  onClose?: () => void
}

const LessonStatsDisplay: React.FC<LessonStatsDisplayProps> = ({
  stats,
  duration,
  accuracy,
  progress,
  onClose
}) => {
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins} phút ${secs} giây`
  }

  const getAccuracyColor = (accuracy: number): string => {
    if (accuracy >= 80) return 'text-green-600'
    if (accuracy >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getAccuracyBg = (accuracy: number): string => {
    if (accuracy >= 80) return 'bg-green-100 border-green-300'
    if (accuracy >= 60) return 'bg-yellow-100 border-yellow-300'
    return 'bg-red-100 border-red-300'
  }

  const getMessage = (accuracy: number): string => {
    if (accuracy >= 90) return 'Xuất sắc! 🎉'
    if (accuracy >= 80) return 'Rất tốt! 👍'
    if (accuracy >= 70) return 'Tốt! 😊'
    if (accuracy >= 60) return 'Khá! 🙂'
    return 'Cần cố gắng thêm! 💪'
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Trophy className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Thống kê Bài học</h2>
                <p className="text-blue-100 text-sm">Kết quả học tập của bạn</p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white text-3xl leading-none"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="p-6 space-y-6">
          {/* Overall Score */}
          <div className={`text-center p-6 rounded-xl border-2 ${getAccuracyBg(accuracy)}`}>
            <div className={`text-5xl font-bold mb-2 ${getAccuracyColor(accuracy)}`}>
              {accuracy}%
            </div>
            <div className="text-lg font-semibold text-gray-700 mb-1">
              {getMessage(accuracy)}
            </div>
            <div className="text-sm text-gray-600">
              Độ chính xác
            </div>
          </div>

          {/* ✅ SIMPLIFIED Stats Grid - Only 4 main stats */}
          <div className="grid grid-cols-2 gap-4">
            {/* Correct Answers */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div className="text-2xl font-bold text-green-700">
                  {stats.correctAnswers}
                </div>
              </div>
              <div className="text-sm text-gray-600">Số lần chọn đúng</div>
            </div>

            {/* Wrong Answers */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xl">✗</span>
                </div>
                <div className="text-2xl font-bold text-red-700">
                  {stats.wrongAnswers}
                </div>
              </div>
              <div className="text-sm text-gray-600">Số lần chọn sai</div>
            </div>

            {/* Hints Used */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-white" />
                </div>
                <div className="text-2xl font-bold text-yellow-700">
                  {stats.hintsUsed}
                </div>
              </div>
              <div className="text-sm text-gray-600">Số lần hiển thị gợi ý</div>
            </div>

            {/* AI Questions */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="text-2xl font-bold text-purple-700">
                  {stats.aiQuestionsAsked}
                </div>
              </div>
              <div className="text-sm text-gray-600">Số lần hỏi AI</div>
            </div>
          </div>

          {/* ✅ SIMPLIFIED: Only show total answered for reference */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Tổng câu hỏi đã trả lời</div>
              <div className="text-2xl font-bold text-gray-700">
                {stats.correctAnswers + stats.wrongAnswers}
              </div>
              {/* ✅ DEBUG: Hiển thị chi tiết để debug */}
              <div className="text-xs text-gray-500 mt-2">
                Debug: Đúng={stats.correctAnswers}, Sai={stats.wrongAnswers}, Gợi ý={stats.hintsUsed}, AI={stats.aiQuestionsAsked}
              </div>
            </div>
          </div>

          {/* Action Button */}
          {onClose && (
            <button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg"
            >
              Đóng
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default LessonStatsDisplay

